import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineEnvelope, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeSlash, HiOutlineExclamationCircle, HiOutlineArrowLeft, HiOutlineCheckCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import AuthLayout from './AuthLayout';
import { validateEmail, validatePassword, validateConfirmPassword } from '../../utils/validation';
import api from '../../utils/api';

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [pw, setPw] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (timer > 0) { const t = setTimeout(() => setTimer(timer - 1), 1000); return () => clearTimeout(t); }
  }, [timer]);

  const sendOTP = async (e) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setErrors({ email: err }); return; }
    setLoading(true); setServerErr('');
    try {
      const r = await api.post('/auth/forgot-password', { email: email.trim() });
      if (r.data.success) { toast.success('Code sent!'); setStep(2); setTimer(60); setErrors({}); }
    } catch (e) { setServerErr(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const onOtpChange = (i, v) => {
    if (v && !/^\d$/.test(v)) return;
    const n = [...otp]; n[i] = v; setOtp(n);
    if (errors.otp) setErrors({});
    if (serverErr) setServerErr('');
    if (v && i < 5) refs.current[i + 1]?.focus();
  };
  const onOtpKey = (i, e) => { if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i - 1]?.focus(); };
  const onOtpPaste = (e) => {
    e.preventDefault();
    const d = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (d.length === 6) { setOtp(d.split('')); refs.current[5]?.focus(); }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    const s = otp.join('');
    if (s.length !== 6) { setErrors({ otp: 'Enter all 6 digits' }); return; }
    setLoading(true); setServerErr('');
    try {
      const r = await api.post('/auth/verify-otp', { email: email.trim(), otp: s });
      if (r.data.success) { toast.success('Verified!'); setResetToken(r.data.resetToken); setStep(3); setErrors({}); }
    } catch (e) { setServerErr(e.response?.data?.message || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const resendOTP = useCallback(async () => {
    if (timer > 0) return; setLoading(true);
    try {
      const r = await api.post('/auth/forgot-password', { email: email.trim() });
      if (r.data.success) { toast.success('New code sent!'); setOtp(['', '', '', '', '', '']); setTimer(60); refs.current[0]?.focus(); }
    } catch (e) { setServerErr(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, [email, timer]);

  const resetPw = async (e) => {
    e.preventDefault();
    const errs = {};
    const pe = validatePassword(pw.password); const ce = validateConfirmPassword(pw.password, pw.confirmPassword);
    if (pe) errs.password = pe; if (ce) errs.confirmPassword = ce;
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true); setServerErr('');
    try {
      const r = await api.post('/auth/reset-password', { resetToken, password: pw.password, confirmPassword: pw.confirmPassword });
      if (r.data.success) { setSuccess(true); toast.success('Password reset!'); }
    } catch (e) { setServerErr(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  if (success) return (
    <AuthLayout>
      <div style={{ textAlign: 'center' }}>
        <div className="success-circle"><HiOutlineCheckCircle /></div>
        <div className="auth-heading" style={{ textAlign: 'center' }}>
          <h2>Password reset!</h2>
          <p>You can now sign in with your new password.</p>
        </div>
        <Link to="/signin"><button className="btn-submit btn-block">Go to sign in</button></Link>
      </div>
    </AuthLayout>
  );

  return (
    <AuthLayout>
      <div className="step-pills">
        <div className={`step-pill ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`} />
        <div className={`step-pill ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`} />
        <div className={`step-pill ${step >= 3 ? 'active' : ''}`} />
      </div>
      {step > 1 && (
        <button className="back-btn" onClick={() => { setStep(step - 1); setServerErr(''); setErrors({}); }}>
          <HiOutlineArrowLeft /> Back
        </button>
      )}
      {serverErr && (
        <div className="alert alert-error">
          <HiOutlineExclamationCircle className="alert-icon" />
          <span>{serverErr}</span>
        </div>
      )}

      {step === 1 && (
        <>
          <div className="auth-heading">
            <h2>Forgot password?</h2>
            <p>Enter your email and we'll send a verification code.</p>
          </div>
          <form className="auth-form" onSubmit={sendOTP} noValidate>
            <div className="field">
              <label className="field-label">Email address</label>
              <div className={`field-shell ${errors.email ? 'error' : ''}`}>
                <span className="field-shell-icon"><HiOutlineEnvelope /></span>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setErrors({}); setServerErr(''); }}
                  autoFocus
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <span className="field-error">
                  <HiOutlineExclamationCircle size={13} /> {errors.email}
                </span>
              )}
            </div>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Send code'}
            </button>
          </form>
          <div className="auth-foot">
            Remember it? <Link to="/signin" className="link">Sign in</Link>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="auth-heading">
            <h2>Enter code</h2>
            <p>6-digit code sent to <strong>{email}</strong></p>
          </div>
          <form className="auth-form" onSubmit={verifyOTP} noValidate>
            <div className="field">
              <div className="otp-row" onPaste={onOtpPaste}>
                {otp.map((d, i) => (
                  <input
                    key={i}
                    ref={el => refs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className={`otp-input ${errors.otp ? 'error' : ''}`}
                    value={d}
                    onChange={e => onOtpChange(i, e.target.value)}
                    onKeyDown={e => onOtpKey(i, e)}
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              {errors.otp && (
                <span className="field-error" style={{ justifyContent: 'center' }}>
                  <HiOutlineExclamationCircle size={13} /> {errors.otp}
                </span>
              )}
            </div>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Verify'}
            </button>
            <div className="otp-resend">
              {timer > 0
                ? <span className="field-helper">Resend in {timer}s</span>
                : <button type="button" className="otp-resend-btn" onClick={resendOTP} disabled={loading}>Resend code</button>
              }
            </div>
          </form>
        </>
      )}

      {step === 3 && (
        <>
          <div className="auth-heading">
            <h2>New password</h2>
            <p>Create a strong password</p>
          </div>
          <form className="auth-form" onSubmit={resetPw} noValidate>
            <div className="field">
              <label className="field-label">New password</label>
              <div className={`field-shell ${errors.password ? 'error' : ''}`}>
                <span className="field-shell-icon"><HiOutlineLockClosed /></span>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={pw.password}
                  onChange={e => { setPw(p => ({ ...p, password: e.target.value })); setErrors(p => ({ ...p, password: '' })); }}
                  autoFocus
                />
                <button type="button" className="field-shell-action" onClick={() => setShowPw(s => !s)} tabIndex={-1}>
                  {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
              {errors.password && (
                <span className="field-error">
                  <HiOutlineExclamationCircle size={13} /> {errors.password}
                </span>
              )}
            </div>

            <div className="field">
              <label className="field-label">Confirm password</label>
              <div className={`field-shell ${errors.confirmPassword ? 'error' : ''}`}>
                <span className="field-shell-icon"><HiOutlineLockClosed /></span>
                <input
                  type={showCpw ? 'text' : 'password'}
                  placeholder="Re-type password"
                  value={pw.confirmPassword}
                  onChange={e => { setPw(p => ({ ...p, confirmPassword: e.target.value })); setErrors(p => ({ ...p, confirmPassword: '' })); }}
                />
                <button type="button" className="field-shell-action" onClick={() => setShowCpw(s => !s)} tabIndex={-1}>
                  {showCpw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="field-error">
                  <HiOutlineExclamationCircle size={13} /> {errors.confirmPassword}
                </span>
              )}
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Reset password'}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
